import { Observable } from 'rxjs';
import { stringify } from 'query-string';

import { mutate, apolloClient } from 'entities/graphql';
import {
  PersistGovernmentInformationLetterDocument,
  RequestVerificationDocument,
  SearchNonprofitsDocument,
  VerifyOrganizerUserWithPayPalDocument,
} from 'generated/graphql';
import UserApi from 'entities/user/api';

import Api from '../api';
import { updateOrganizationAction } from '../user/actions';

import Organization from './model';

export type NonprofitSearchResult = {
  id: string;
  name: string;
  ein: string;
  approved: boolean;
  locationObject?: {
    city?: string;
    address?: string;
    postalCode?: string;
    state?: string;
  };
};

export default class OrganizationApi {
  static fetchNonprofits(search: string, selected: string, query: Record<string, any>) {
    return Api.get(
      `api/organizations/nonprofits?${stringify(
        {
          search,
          selected,
          ...query,
        },
        {
          arrayFormat: 'comma',
        }
      )}`
    );
  }

  static fetchCompanies() {
    return Api.get('api/organizations/companiesWithAffiliationVisibility');
  }

  static fetchSsoCompanies() {
    return Api.get('api/organizations/companies');
  }

  static fetchAll(search?: string) {
    if (search) {
      return Api.get(
        `api/organizations?${stringify(
          {
            search,
          },
          {
            arrayFormat: 'comma',
          }
        )}`
      );
    }
    return Api.get('api/organizations');
  }

  static searchNonprofits(
    variables: {
      searchTerm: string;
      countryCode?: string;
      donationProvider?: string;
      stateCode?: string; // state code is prefixed by country code
      includeBlocked?: boolean;
      includeApproved?: boolean;
      acceptsDonations?: boolean;
      excludeDonationProviders?: string[];
      cause?: string;
    } = { searchTerm: '', excludeDonationProviders: [] },
    useNonprofitDbSearch = false
  ) {
    if (useNonprofitDbSearch) {
      return Observable.from(
        apolloClient.query({
          query: SearchNonprofitsDocument,
          variables,
        })
      ).map(({ data: { searchNonprofits } }) => searchNonprofits);
    }

    return Api.get(
      `api/organizations/searchNonprofits/?${stringify(
        {
          ...variables,
          includeApproved:
            variables.includeApproved === undefined ? true : variables.includeApproved,
        },
        {
          arrayFormat: 'comma',
          skipEmptyString: true,
          skipNull: true,
        }
      )}`
    );
  }

  static listNonprofits(
    searchTerm: string,
    countryCode: string,
    acceptsDonations: boolean,
    stateCode = null,
    pageSize = 20
  ) {
    return Api.get(
      `api/organizations/nonprofits/list/?${stringify({
        includeApproved: true,
        searchTerm,
        countryCode,
        stateCode,
        acceptsDonations,
        pageSize,
      })}`
    );
  }

  static update(id: string, data: Record<string, any>) {
    return Api.patch(`api/organizations/${id}`, data).map((organization) =>
      updateOrganizationAction(new Organization(organization))
    );
  }

  static unblockNonprofit(nonprofit: string) {
    return Api.post(`api/organizations/unblock/${nonprofit}`);
  }

  static authorizeStripe(data: Record<string, any>) {
    return Api.post('api/organizations/authorizeStripe', data).map((organization) =>
      updateOrganizationAction(new Organization(organization))
    );
  }

  static createNewCompany(_companyId: string, data: Record<string, any>) {
    return Api.post(`api/organizations/company/new`, data);
  }

  static cloneCompany(companyId: string, data: Record<string, any>) {
    return Api.post(`api/organizations/company/${companyId}/clone`, data);
  }

  static fetchDepartments() {
    return Api.get('api/organizations/departments');
  }

  static fetchJobTitles() {
    return Api.get('api/organizations/jobTitles');
  }

  static fetchNonprofit(id: string, query = {}) {
    return Api.get(`api/organizations/${id}?${stringify(query)}`);
  }

  static fetchCompanyDonationsTotal() {
    return Api.get(`api/organizations/donations/total`);
  }

  static fetchCompanyDonationsTotalById(id: string) {
    return Api.get(`api/organizations/${id}/donations/total`);
  }

  static fetchCompanyVolunteerTimesTotal() {
    return Api.get(`api/organizations/volunteer-times/total`);
  }

  static fetchCompanyVolunteerTimesTotalById(id: string) {
    return Api.get(`api/organizations/${id}/volunteer-times/total`);
  }

  static fetchCompanyDeedsTotal() {
    return Api.get(`api/organizations/deeds/total`);
  }

  static fetchCompanyDeedsTotalById(id: string) {
    return Api.get(`api/organizations/${id}/deeds/total`);
  }

  static importOrUpdateGiveIndiaNonprofits(data: Record<string, any>) {
    return Api.post(`api/organizations/importOrUpdateGiveIndiaNonprofits`, data);
  }

  static checkIfClaimed(id: string) {
    return Api.get(`api/organizations/checkIfClaimed/${id}`);
  }

  static verifyNonprofitWithPaypal(code: string) {
    return Observable.from(mutate(VerifyOrganizerUserWithPayPalDocument, { code }))
      .map(({ errors, data }) => {
        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (!data) {
          throw new Error('Error verifying nonprofit organizer user with PayPal');
        }

        return true;
      })
      .mergeMap(() => UserApi.fetch());
  }

  static persistGovernmentInformationLetter(
    governmentInformationLetterUri: string,
    letterOfAssociationUri: string,
    legalName: string
  ) {
    return Observable.from(
      mutate(PersistGovernmentInformationLetterDocument, {
        governmentInformationLetterUri,
        letterOfAssociationUri,
        legalName,
      })
    ).map(({ errors, data }) => {
      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (!data) {
        throw new Error('Error persisting Government Information Letter');
      }

      return true;
    });
  }

  static requestVerification(supportingDocuments: string[], role: string) {
    return Observable.from(mutate(RequestVerificationDocument, { supportingDocuments, role })).map(
      ({ errors, data }) => {
        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (!data) {
          throw new Error('Error requesting manual verification');
        }

        return true;
      }
    );
  }
}
